import type { FC } from 'react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CssBaseline } from '@mui/material';

import { Header } from '/common/Header';
import { NavSidebar } from '/common/NavSidebar';
import { RoutesList } from '/Router/RoutesList';
import type { IRouteComponentProps } from '/Router/models';
import { Helper } from '/common/Helper';
import { AppTimeline } from '/common/AppTimeline';
import { ProgressIndicatorProvider } from '/common/ProgressIndicator';
import { uiToolkit } from '/store/toolkits';

import { AppContent, RootComponent } from './styles';

export const AppPage: FC<IRouteComponentProps> = ({ routes = [], location }) => {
  const isNavSidebarPermanentlyOpen: boolean = useSelector(uiToolkit.selectors.navSidebarOpen);
  const timelineState = useSelector(uiToolkit.selectors.timeline);
  const isTimelineOpen = timelineState.open;

  const [isNavSidebarOpen, setIsNavSidebarOpen] = useState(isNavSidebarPermanentlyOpen);
  const [timelineWasOpened, setTimelineWasOpened] = useState(false);

  useEffect(() => {
    if (isTimelineOpen && !timelineWasOpened) {
      setTimelineWasOpened(true);
    }
  }, [isTimelineOpen, timelineWasOpened]);
  const routerList = useMemo(() => <RoutesList routes={routes} />, [routes]);

  const handleNavSidebarOpeningStatusChange = useCallback((isOpen: boolean) => {
    setIsNavSidebarOpen(isOpen);
  }, []);

  return (
    <RootComponent>
      <CssBaseline />
      <ProgressIndicatorProvider>
        <Header />
      </ProgressIndicatorProvider>
      <NavSidebar location={location} onOpeningStatusChange={handleNavSidebarOpeningStatusChange} />
      <AppContent>
        {routerList}
        <Helper navSidebarOpen={isNavSidebarOpen} />
      </AppContent>
      {(isTimelineOpen || timelineWasOpened) && (
        <Suspense fallback={null}>
          <AppTimeline />
        </Suspense>
      )}
    </RootComponent>
  );
};
